/**
 * SessionExpiredProvider — turns a dead Auth0 session into a recoverable, one-click re-login instead of
 * a dead-end "Something went wrong" card that only incognito could clear.
 *
 * Wiring: the QueryCache/MutationCache onError hooks (App.tsx) call reportIfSessionExpired() on any 401;
 * that routes to the handler this provider registers. The handler decides:
 *   - no token was sent AND the renewal failed for a reason that needs an interactive login
 *     (signal.renewalFailure === "dead": login_required / invalid_grant / missing_refresh_token / …),
 *     or the request captured no renewal verdict at all → show the blocking overlay immediately;
 *   - a token WAS sent (maybe a transient reject: Auth0 key rotation / clock skew / a backend restart
 *     window) → try ONE silent forced refresh; if it works, refetch and stay in the app; only if it
 *     fails show the overlay. Bounded to a single silent attempt per mount so a persistent
 *     token-rejected-by-backend (audience/issuer misconfig) can't spin an invisible refresh loop, and
 *     deadlined (SILENT_REFRESH_DEADLINE_MS) so a wedged renewal can't hold every concurrent 401 in
 *     limbo for the ~35s auth0-spa-js is willing to spend on retries.
 *   - no token was sent because the renewal hit something self-healing (renewalFailure === "transient":
 *     a Web Locks contention timeout, an offline blip, a DNS hiccup, an Auth0 429) → the same silent
 *     forced refresh. This is the whole point: without it a two-second blip is indistinguishable from a
 *     revoked refresh token and ejects a user whose session is good for another two weeks. Bounded by a
 *     COOLDOWN (one recovery per TRANSIENT_RECOVERY_COOLDOWN_MS) rather than by the per-mount count —
 *     see the long note at the gate for why the two paths must not share a budget.
 *   - a 401 arriving right after an INTERACTIVE login (within CONFIG_LOOP_WINDOW_MS) → the login itself
 *     yields a rejected token → a config problem, not an expiry: show a terminal "sign-in problem"
 *     state with NO "Sign in" button so the user can't bounce forever.
 *   - a signal marked `userInitiated` (the "Sign in" button on an ErrorState / NoAccess card, which
 *     travels this same channel) → the overlay, immediately and unconditionally. It is checked ahead of
 *     the in-flight gate: a silent recovery may hold that gate for seconds, and a click swallowed there
 *     is a button that visibly does nothing.
 *
 * In MOCK / DEV_BYPASS mode there is no Auth0Provider ancestor, so this must NOT call useAuth0() — the
 * mock branch registers a plain reload handler and renders children only (mirrors AuthProvider).
 */

import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import { useQueryClient } from "@tanstack/react-query";
import { ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui";
import { USE_MOCK_API, DEV_BYPASS_AUTH } from "@/lib/env";
import { setSessionExpiredHandler, type SessionExpiredSignal } from "./sessionExpiredSignal";

/** Timestamp (ms) of the last user-initiated login redirect — survives the redirect via sessionStorage. */
const LAST_LOGIN_KEY = "portal:lastInteractiveLoginAt";
/** A 401 within this window of an interactive login = the login yields a rejected token = a config
 *  problem, not an ordinary expiry. */
const CONFIG_LOOP_WINDOW_MS = 20_000;
/** How often the TRANSIENT path may buy itself a silent recovery. Not a per-mount count: staff keep this
 *  portal open all day and pool screens poll every 60s, so a once-per-mount budget is spent hours before
 *  the blip that matters. A rate bound retries the same blip once and still works at 15:30. */
const TRANSIENT_RECOVERY_COOLDOWN_MS = 5 * 60_000;
/**
 * How long the silent recovery may run before we stop waiting for it.
 *
 * `getAccessTokenSilently({cacheMode:"off"})` carries no timeout of its own: it goes straight to
 * `runWithLock(lockKey, 5000)` and then `getJSON`, which retries a throwing fetch 3× at a 10s timeout
 * each — so on a flaky network the "recovery" can sit there for ~35 seconds. While it does, `busyRef`
 * swallows every other 401, so the user sees neither data nor the overlay, where the pre-change code
 * showed the overlay immediately. Five seconds is comfortably longer than a healthy token round-trip and
 * short enough that a wedged one degrades to the OLD behaviour rather than to a blank screen. Blowing
 * the deadline rejects like any other failed refresh and falls through to the overlay.
 */
const SILENT_REFRESH_DEADLINE_MS = 5_000;

/**
 * Race a promise against a deadline. The timer is cleared on EVERY exit — a stray 5s timeout outlives
 * the test that created it and fires into a torn-down tree. The losing promise is left running on
 * purpose: `Promise.race` has already attached handlers to it, so a late rejection is handled and can
 * never surface as an unhandled rejection.
 */
function withDeadline<T>(work: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new Error("silent refresh exceeded its deadline")), ms);
  });
  return Promise.race([work, deadline]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  });
}

type Phase = "ok" | "expired" | "config";

export function SessionExpiredProvider({ children }: { children: ReactNode }) {
  // SessionExpiredProvider itself calls no hooks before this branch, so the conditional return is safe;
  // each child component below calls its hooks unconditionally.
  if (USE_MOCK_API || DEV_BYPASS_AUTH) {
    return <MockSessionExpired>{children}</MockSessionExpired>;
  }
  return <RealSessionExpired>{children}</RealSessionExpired>;
}

/** Mock/bypass mode has no real Auth0 session and never legitimately 401s; a stray signal just resets. */
function MockSessionExpired({ children }: { children: ReactNode }) {
  useLayoutEffect(() => {
    setSessionExpiredHandler(() => {
      if (typeof window !== "undefined") window.location.assign("/");
    });
    return () => setSessionExpiredHandler(null);
  }, []);
  return <>{children}</>;
}

function RealSessionExpired({ children }: { children: ReactNode }) {
  const { loginWithRedirect, logout, getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<Phase>("ok");

  // The handler is registered once; latest values reach it through refs so it never goes stale and we
  // never re-register (which would drop a parked signal).
  const phaseRef = useRef<Phase>("ok");
  phaseRef.current = phase;
  const authRef = useRef({ getAccessTokenSilently, queryClient });
  authRef.current = { getAccessTokenSilently, queryClient };
  // A silent confirmation is in flight — hold concurrent 401s so one token decision serves them all.
  // Held only across the TOKEN call, not the refetch that follows it, and never applied to a user's own
  // "Sign in" click; both reasons are spelled out at the gate below.
  const busyRef = useRef(false);
  const confirmedRef = useRef(false); // at most one silent confirmation per mount (token-present path)
  const transientRecoveryAtRef = useRef(0); // when the transient path last spent its cooldown

  useLayoutEffect(() => {
    const onSignal = async (signal: SessionExpiredSignal) => {
      if (phaseRef.current !== "ok") return; // idempotent — overlay already up

      const lastLogin = Number(sessionStorage.getItem(LAST_LOGIN_KEY) ?? 0);
      if (lastLogin && Date.now() - lastLogin < CONFIG_LOOP_WINDOW_MS) {
        setPhase("config");
        return;
      }

      // A human pressed "Sign in" (ErrorState / NoAccess). Checked BEFORE the busy gate on purpose: a
      // click and a 401 share this one channel, and the gate below is written to drop signals while a
      // silent recovery runs. Dropping a 401 there is the intent — one confirmation decides for all of
      // them. Dropping a CLICK is a dead button: the recovery's refetch takes a second or three, the
      // screen still shows the 401 error cards for all of it, and every press the user makes in that
      // window vanishes with no overlay, no spinner, nothing. Before the transient branch existed this
      // could barely happen (a no-token 401 ejected immediately and opened no busy window at all); now
      // one opens on every transient no-token 401, once per cooldown, for the life of the mount.
      //
      // A deliberate click is also not worth a silent refresh even when the gate is open: the user is
      // telling us they want to re-authenticate, and "recovering" behind their back re-renders the same
      // dead error card they just clicked out of. Straight to the overlay, spending neither budget.
      if (signal.userInitiated) {
        setPhase("expired");
        return;
      }

      if (busyRef.current) return; // a confirmation is deciding for all concurrent 401s

      // Two ways in, TWO budgets — deliberately not one shared bound.
      //
      // The token-present path CAN loop: Auth0 keeps issuing a token the backend keeps rejecting (an
      // audience/issuer misconfig), so every silent refresh "succeeds" and the next 401 looks identical.
      // Nothing about that is self-limiting, so it needs the hard once-per-mount cap — confirmedRef.
      //
      // The transient path cannot loop that way. Once its silent refresh succeeds the next request
      // carries a Bearer, so a repeat arrives as tokenWasSent=true and is judged by the branch above.
      // Sharing confirmedRef with it only means spending the budget on the FIRST blip of the day: in this
      // portal tabs stay open for hours and pool screens poll every 60s, so a 10:14 lock timeout recovers
      // invisibly and then the identical two-second Wi-Fi drop at 15:30 ejects a user with two weeks of
      // refresh-token life left — precisely the ejection this change exists to prevent. It gets a rate
      // bound instead: one recovery per cooldown window. That is a sufficient loop bound on its own,
      // because a recovery that FAILS ends in the overlay — phase leaves "ok", so every later signal
      // returns at the top of this handler and no second attempt is ever reached.
      const now = Date.now();
      const retryTokenReject = signal.tokenWasSent && !confirmedRef.current;
      const retryTransient =
        !signal.tokenWasSent &&
        signal.renewalFailure === "transient" &&
        now - transientRecoveryAtRef.current >= TRANSIENT_RECOVERY_COOLDOWN_MS;

      if (retryTokenReject || retryTransient) {
        if (retryTokenReject) confirmedRef.current = true;
        else transientRecoveryAtRef.current = now;
        busyRef.current = true;
        try {
          // Deadlined, not awaited open-endedly: this one call is holding back every concurrent 401.
          await withDeadline(
            authRef.current.getAccessTokenSilently({ cacheMode: "off" }),
            SILENT_REFRESH_DEADLINE_MS,
          );
          // Released HERE — the instant the TOKEN lands, never after the refetch below.
          //
          // busyRef exists to make one token decision serve every 401 that arrived together. The token
          // result IS that decision, so once it is in hand the gate has nothing left to protect. The
          // refetch is a different animal: it is `fetch`, which has no timeout of its own, so a server
          // that accepts the connection and never answers leaves invalidateQueries pending for the rest
          // of the page's life. Holding the gate across it would swallow every later 401 AND every
          // "Sign in" click for that whole time — the overlay could never be raised again on this mount
          // and the user's only escape would be a manual browser reload. The deadline above bounds only
          // its own half; this release is what bounds the other.
          //
          // Re-entering the gate after this point is safe and bounded: the refresh succeeded, so later
          // requests carry a Bearer and their 401s arrive on the tokenWasSent path, which confirmedRef
          // caps at exactly one silent attempt per mount.
          busyRef.current = false;
          await authRef.current.queryClient.invalidateQueries();
          return; // recovered — the refetch will replace the error state
        } catch {
          busyRef.current = false;
          // the refresh itself failed → the session really is dead → fall through
        }
      }

      setPhase("expired");
    };

    setSessionExpiredHandler((signal) => void onSignal(signal));
    return () => setSessionExpiredHandler(null);
  }, []);

  const onSignIn = useCallback(() => {
    // Stamp the interactive login so an immediate post-login 401 is read as a config loop, not an expiry.
    try {
      sessionStorage.setItem(LAST_LOGIN_KEY, String(Date.now()));
    } catch {
      /* sessionStorage unavailable (private mode edge) — the guard just won't engage */
    }
    void loginWithRedirect({ authorizationParams: { prompt: "login" } });
  }, [loginWithRedirect]);

  const onSignOut = useCallback(() => {
    void logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  return (
    <>
      {children}
      {phase !== "ok" && (
        <SessionExpiredOverlay phase={phase} onSignIn={onSignIn} onSignOut={onSignOut} />
      )}
    </>
  );
}

function SessionExpiredOverlay({
  phase,
  onSignIn,
  onSignOut,
}: {
  phase: Exclude<Phase, "ok">;
  onSignIn: () => void;
  onSignOut: () => void;
}) {
  const { t } = useTranslation();
  const config = phase === "config";

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-expired-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[oklch(0.2_0.02_265_/_0.55)] px-4 backdrop-blur-sm"
    >
      <div className="flex w-full max-w-sm flex-col items-center gap-5 rounded-(--radius-lg) border border-(--border) bg-(--surface) px-8 py-10 text-center shadow-(--shadow-lg)">
        <span
          className="flex size-12 items-center justify-center rounded-full bg-(--warn-subtle) text-(--warn)"
          aria-hidden
        >
          <ShieldAlert className="size-6" />
        </span>
        <div className="flex flex-col gap-1.5">
          <h2
            id="session-expired-title"
            className="text-(length:--text-lg) font-semibold text-(--text-strong)"
          >
            {t(config ? "auth.sessionConfigTitle" : "auth.sessionExpiredTitle")}
          </h2>
          <p className="text-(length:--text-sm) text-(--text-muted)">
            {t(config ? "auth.sessionConfigBody" : "auth.sessionExpiredBody")}
          </p>
        </div>

        <div className="flex w-full flex-col gap-2">
          {!config && (
            <Button size="lg" onClick={onSignIn} className="w-full" autoFocus>
              {t("auth.signIn")}
            </Button>
          )}
          <Button variant="secondary" onClick={onSignOut} className="w-full">
            {config ? t("auth.signOut") : t("auth.signInAnother")}
          </Button>
        </div>
      </div>
    </div>
  );
}
